import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Link2, Plus, Pencil, Trash2, Copy, ExternalLink, Save, Loader2, ArrowLeft, Check, Eye } from 'lucide-react';
import { toast } from 'sonner';
import {
  listCollections, createCollection, updateCollection, deleteCollection,
} from '@/lib/collectionsApi';

const emptyForm = () => ({
  slug: '',
  business_heading: 'Flex Living',
  message: '',
  contact: { name: '', phone: '', email: '', whatsapp: '', best_times: '' },
  listing_ids: [],
});

function relativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffSec = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (diffSec < 60) return 'just now';
  const min = Math.round(diffSec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.round(hr / 24);
  if (d < 7) return `${d}d ago`;
  if (d < 30) return `${Math.round(d / 7)}w ago`;
  return new Date(iso).toLocaleDateString();
}

export function CollectionsManagerDialog({ open, onOpenChange, adminPass, allListings }) {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('list');        // 'list' | 'edit'
  const [editing, setEditing] = useState(null);    // null when creating, doc when editing
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [search, setSearch] = useState('');

  // When REACT_APP_SHARE_DOMAIN is set, copied links use the clean format
  // `https://<share-domain>/<slug>` — with no `/c/` prefix — so recipients
  // can't strip anything to reach the internal app.
  const shareDomain = (process.env.REACT_APP_SHARE_DOMAIN || '').trim();
  const shareBase = shareDomain
    ? `https://${shareDomain}/`
    : (typeof window !== 'undefined' ? `${window.location.origin}/c/` : '/c/');

  const reload = useCallback(async () => {
    if (!adminPass) return;
    setLoading(true);
    try {
      setCollections(await listCollections(adminPass));
    } catch (e) {
      toast.error(e.message || 'Failed to load links');
    } finally {
      setLoading(false);
    }
  }, [adminPass]);

  useEffect(() => { if (open) { reload(); setView('list'); } }, [open, reload]);

  const startCreate = () => { setEditing(null); setForm(emptyForm()); setView('edit'); };
  const startEdit = (c) => {
    setEditing(c);
    setForm({
      slug: c.slug,
      business_heading: c.business_heading || 'Flex Living',
      message: c.message || '',
      contact: { name: '', phone: '', email: '', whatsapp: '', best_times: '', ...(c.contact || {}) },
      listing_ids: [...(c.listing_ids || [])],
    });
    setView('edit');
  };

  const toggleListing = (id) => {
    setForm(f => {
      const exists = f.listing_ids.includes(id);
      return { ...f, listing_ids: exists ? f.listing_ids.filter(x => x !== id) : [...f.listing_ids, id] };
    });
  };

  const moveListing = (id, dir) => {
    setForm(f => {
      const arr = [...f.listing_ids];
      const idx = arr.indexOf(id);
      if (idx === -1) return f;
      const swap = idx + dir;
      if (swap < 0 || swap >= arr.length) return f;
      [arr[idx], arr[swap]] = [arr[swap], arr[idx]];
      return { ...f, listing_ids: arr };
    });
  };

  const handleSave = async () => {
    const payload = {
      slug: form.slug.trim().toLowerCase().replace(/\s+/g, '-'),
      business_heading: form.business_heading.trim() || 'Flex Living',
      message: form.message,
      contact: form.contact,
      listing_ids: form.listing_ids,
    };
    if (!/^[a-z0-9][a-z0-9-]{1,78}[a-z0-9]$/.test(payload.slug)) {
      toast.error('Slug must be 3-80 chars: lowercase letters, numbers, hyphens.');
      return;
    }
    if (payload.listing_ids.length === 0) {
      toast.error('Attach at least one listing');
      return;
    }
    setSaving(true);
    try {
      if (editing) await updateCollection(adminPass, editing.id, payload);
      else await createCollection(adminPass, payload);
      toast.success(editing ? 'Link updated' : 'Link created');
      await reload();
      setView('list');
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete the link "${c.slug}"? This cannot be undone.`)) return;
    setDeletingId(c.id);
    try {
      await deleteCollection(adminPass, c.id);
      toast.success('Link deleted');
      await reload();
    } catch (e) {
      toast.error(e.message || 'Delete failed');
    } finally {
      setDeletingId(null);
    }
  };

  const copyUrl = (slug) => {
    const url = `${shareBase}${slug}`;
    navigator.clipboard.writeText(url);
    toast.success('Link copied');
  };

  // ── Listing picker helpers ────────────────────────────────────────────
  const selected = form.listing_ids
    .map(id => allListings.find(l => l.id === id))
    .filter(Boolean);
  const available = allListings
    .filter(l => !form.listing_ids.includes(l.id))
    .filter(l => {
      const q = search.trim().toLowerCase();
      if (!q) return true;
      return (l.title || '').toLowerCase().includes(q)
        || (l.property_name || '').toLowerCase().includes(q)
        || (l.unit_number || '').toLowerCase().includes(q);
    });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-hidden flex flex-col" data-testid="collections-manager-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {view === 'edit' && (
              <button onClick={() => setView('list')} className="text-muted-foreground hover:text-foreground" data-testid="collections-back-btn">
                <ArrowLeft className="h-4 w-4" />
              </button>
            )}
            <Link2 className="h-4 w-4 text-primary" />
            {view === 'list' ? 'Custom Share Links' : (editing ? 'Edit link' : 'Create link')}
          </DialogTitle>
          <DialogDescription>
            {view === 'list'
              ? 'Bundle a few listings + a message + your contact info and send one URL to a lead.'
              : 'Recipients only see the listings you attach here — nothing else from the app.'}
          </DialogDescription>
        </DialogHeader>

        {view === 'list' && (
          <>
            <div className="flex items-center justify-between pb-2 border-b border-border/40">
              <p className="text-xs text-muted-foreground">{collections.length} link{collections.length === 1 ? '' : 's'}</p>
              <Button size="sm" onClick={startCreate} data-testid="collections-new-btn"><Plus className="h-3.5 w-3.5 mr-1" />New link</Button>
            </div>
            <div className="flex-1 overflow-y-auto -mx-6 px-6">
              {loading ? (
                <div className="py-12 text-center text-muted-foreground text-sm"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
              ) : collections.length === 0 ? (
                <div className="py-12 text-center text-sm text-muted-foreground" data-testid="collections-empty">
                  No custom links yet. Create your first one to share a curated set of listings with a lead.
                </div>
              ) : (
                <div className="space-y-2 py-2">
                  {collections.map(c => (
                    <div key={c.id} className="border border-border/60 rounded-lg p-3 bg-white" data-testid="collection-row">
                      <div className="flex items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm truncate" data-testid="collection-heading">{c.business_heading || 'Flex Living'}</p>
                          <p className="text-xs text-muted-foreground mt-0.5 truncate">
                            <span className="font-mono">/c/{c.slug}</span> · {c.listing_ids?.length || 0} listing{c.listing_ids?.length === 1 ? '' : 's'}
                          </p>
                          <div className="flex items-center gap-3 mt-1" data-testid="collection-view-stats">
                            <span className={`inline-flex items-center gap-1 text-[11px] font-medium rounded-full px-2 py-0.5 ${(c.view_count || 0) > 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-muted text-muted-foreground'}`} data-testid="collection-view-count">
                              <Eye className="h-3 w-3" />
                              {c.view_count || 0} view{c.view_count === 1 ? '' : 's'}
                            </span>
                            {c.last_viewed_at ? (
                              <span className="text-[11px] text-muted-foreground" data-testid="collection-last-viewed" title={c.last_viewed_at}>
                                Last opened {relativeTime(c.last_viewed_at)}
                              </span>
                            ) : (
                              <span className="text-[11px] text-muted-foreground italic" data-testid="collection-never-viewed">Not opened yet</span>
                            )}
                          </div>
                          {c.message && <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{c.message}</p>}
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                          <Button variant="outline" size="sm" className="h-7 gap-1 text-xs" onClick={() => copyUrl(c.slug)} data-testid="collection-copy-btn">
                            <Copy className="h-3 w-3" />Copy
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 gap-1 text-xs"
                            title="Preview the exact page the guest will see (opens in a new tab)"
                            onClick={() => window.open(`${shareBase}${c.slug}`, '_blank', 'noopener,noreferrer')}
                            data-testid="collection-preview-btn"
                          >
                            <ExternalLink className="h-3 w-3" />Preview as guest
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Edit" onClick={() => startEdit(c)} data-testid="collection-edit-btn">
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" title="Delete" onClick={() => handleDelete(c)} disabled={deletingId === c.id} data-testid="collection-delete-btn">
                            {deletingId === c.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {view === 'edit' && (
          <div className="flex-1 overflow-y-auto -mx-6 px-6 space-y-4 py-2">
            <div>
              <Label className="text-xs">Slug (the share URL will be {shareBase}<span className="font-semibold">your-slug</span>)</Label>
              <Input
                value={form.slug}
                onChange={e => setForm(f => ({ ...f, slug: e.target.value }))}
                placeholder="e.g. ari-spring-2026"
                className="mt-1 font-mono text-xs"
                data-testid="collection-slug-input"
              />
              <p className="text-[10px] text-muted-foreground mt-1">Lowercase letters, numbers, and hyphens only. Non-guessable slugs = more private.</p>
            </div>
            <div>
              <Label className="text-xs">Business heading (shown at the top)</Label>
              <Input value={form.business_heading} onChange={e => setForm(f => ({ ...f, business_heading: e.target.value }))} className="mt-1" data-testid="collection-heading-input" />
            </div>
            <div>
              <Label className="text-xs">Message to recipient (shown under heading)</Label>
              <Textarea
                value={form.message}
                onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                placeholder="e.g. Hey Ari, here are a few options that match what we discussed…"
                className="mt-1 text-sm"
                rows={3}
                data-testid="collection-message-input"
              />
            </div>

            <div className="rounded-lg border border-border/60 bg-[#faf8f4] p-3 space-y-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Contact info — recipients will see: "Please reach out to [name]"
              </p>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-[10px]">Your name</Label><Input value={form.contact.name} onChange={e => setForm(f => ({ ...f, contact: { ...f.contact, name: e.target.value } }))} className="h-8 text-xs mt-0.5" data-testid="collection-contact-name" /></div>
                <div><Label className="text-[10px]">Phone</Label><Input value={form.contact.phone} onChange={e => setForm(f => ({ ...f, contact: { ...f.contact, phone: e.target.value } }))} className="h-8 text-xs mt-0.5" data-testid="collection-contact-phone" /></div>
                <div><Label className="text-[10px]">Email</Label><Input value={form.contact.email} onChange={e => setForm(f => ({ ...f, contact: { ...f.contact, email: e.target.value } }))} className="h-8 text-xs mt-0.5" data-testid="collection-contact-email" /></div>
                <div><Label className="text-[10px]">WhatsApp</Label><Input value={form.contact.whatsapp} onChange={e => setForm(f => ({ ...f, contact: { ...f.contact, whatsapp: e.target.value } }))} className="h-8 text-xs mt-0.5" data-testid="collection-contact-whatsapp" /></div>
                <div className="col-span-2"><Label className="text-[10px]">Best times to reach you</Label><Input value={form.contact.best_times} onChange={e => setForm(f => ({ ...f, contact: { ...f.contact, best_times: e.target.value } }))} placeholder="e.g. Mon-Fri 9am-7pm EST" className="h-8 text-xs mt-0.5" data-testid="collection-contact-times" /></div>
              </div>
            </div>

            <div>
              <Label className="text-xs">Attached listings ({selected.length})</Label>
              <div className="mt-2 space-y-1" data-testid="collection-attached-list">
                {selected.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic px-2 py-4 text-center border border-dashed rounded">Pick listings from the library below.</p>
                ) : (
                  selected.map((l, idx) => (
                    <div key={l.id} className="flex items-center gap-2 p-2 border border-border/60 rounded bg-white" data-testid="collection-attached-item">
                      <span className="text-[10px] text-muted-foreground tabular-nums w-5">{idx + 1}.</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm truncate">{l.title}</p>
                        <p className="text-[10px] text-muted-foreground truncate">
                          {l.property_name || '—'}{l.unit_number ? ` · Unit ${l.unit_number}` : ''}
                        </p>
                      </div>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => moveListing(l.id, -1)} disabled={idx === 0}>↑</Button>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => moveListing(l.id, 1)} disabled={idx === selected.length - 1}>↓</Button>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-destructive" onClick={() => toggleListing(l.id)}><Trash2 className="h-3 w-3" /></Button>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div>
              <Label className="text-xs">Add from library</Label>
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search property or unit…" className="mt-1 h-8 text-xs" data-testid="collection-library-search" />
              <div className="mt-1 max-h-48 overflow-y-auto border border-border/60 rounded divide-y">
                {available.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic text-center py-4">No matching listings.</p>
                ) : (
                  available.map(l => (
                    <button
                      key={l.id}
                      type="button"
                      className="w-full text-left flex items-center justify-between gap-2 px-2 py-1.5 hover:bg-muted/40 transition-colors"
                      onClick={() => toggleListing(l.id)}
                      data-testid="collection-library-item"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-xs truncate">{l.title}</p>
                        <p className="text-[10px] text-muted-foreground truncate">
                          {l.property_name || '—'}{l.unit_number ? ` · Unit ${l.unit_number}` : ''}
                        </p>
                      </div>
                      <Plus className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          {view === 'list' ? (
            <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => setView('list')}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving} data-testid="collection-save-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                {editing ? 'Save changes' : 'Create link'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
